import { useEffect, useState } from "react";
import { format } from "date-fns";
import { ExternalLink, Trash2 } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  getBugScreenshotUrl,
  useDeleteBugReport,
  useUpdateBugReportStatus,
  type BugReportWithReporter,
  type BugStatus,
} from "@/hooks/useBugReports";

interface BugDetailSheetProps {
  report: BugReportWithReporter | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const STATUS_LABELS: Record<BugStatus, string> = {
  open: "Open",
  in_progress: "In progress",
  resolved: "Resolved",
  wont_fix: "Won't fix",
};

export function BugDetailSheet({ report, open, onOpenChange }: BugDetailSheetProps) {
  const updateStatus = useUpdateBugReportStatus();
  const deleteReport = useDeleteBugReport();
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (report?.screenshot_path) {
      getBugScreenshotUrl(report.screenshot_path).then((url) => {
        if (!cancelled) setScreenshotUrl(url);
      });
    } else {
      setScreenshotUrl(null);
    }
    return () => {
      cancelled = true;
    };
  }, [report?.id, report?.screenshot_path]);

  if (!report) return null;

  const reporter = report.reporter?.display_name || report.reporter?.email || "Unknown";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader className="space-y-2">
          <div className="flex items-start justify-between gap-4">
            <SheetTitle className="text-left">{report.title}</SheetTitle>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="icon" title="Delete report">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this bug report?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This permanently removes the report and any uploaded screenshot.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() =>
                      deleteReport.mutate(
                        { id: report.id, screenshot_path: report.screenshot_path },
                        { onSuccess: () => onOpenChange(false) },
                      )
                    }
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
          <SheetDescription className="text-left">
            Reported by {reporter} on {format(new Date(report.created_at), "MMM d, yyyy 'at' h:mm a")}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          <div className="flex items-center gap-3">
            <Badge variant="outline">Severity: {report.severity}</Badge>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Status:</span>
              <Select
                value={report.status}
                onValueChange={(v) =>
                  updateStatus.mutate({ id: report.id, status: v as BugStatus })
                }
              >
                <SelectTrigger className="w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(STATUS_LABELS) as BugStatus[]).map((s) => (
                    <SelectItem key={s} value={s}>
                      {STATUS_LABELS[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {report.description && (
            <Section title="Description">
              <p className="whitespace-pre-wrap text-sm">{report.description}</p>
            </Section>
          )}

          {screenshotUrl && (
            <Section title="Screenshot">
              <a href={screenshotUrl} target="_blank" rel="noreferrer" className="block">
                <img
                  src={screenshotUrl}
                  alt="Bug screenshot"
                  className="rounded-md border max-h-96 w-full object-contain bg-muted/30"
                />
              </a>
              {report.screenshot_ocr_status === "pending" && (
                <p className="text-xs text-muted-foreground mt-2">
                  Extracting text from screenshot…
                </p>
              )}
              {report.screenshot_ocr_status === "failed" && report.screenshot_ocr_error && (
                <p className="text-xs text-destructive mt-2">
                  OCR failed: {report.screenshot_ocr_error}
                </p>
              )}
            </Section>
          )}

          {report.screenshot_text && (
            <Section
              title="Extracted text"
              hint={
                report.screenshot_text_confidence !== null
                  ? `Confidence: ${(report.screenshot_text_confidence * 100).toFixed(0)}%`
                  : undefined
              }
            >
              <pre className="whitespace-pre-wrap text-xs bg-muted/30 rounded-md border p-3 max-h-64 overflow-y-auto">
                {report.screenshot_text}
              </pre>
            </Section>
          )}

          <Section title="Page">
            {report.page_url ? (
              <a
                href={report.page_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-sm text-primary hover:underline break-all"
              >
                {report.page_url}
                <ExternalLink className="h-3 w-3 flex-shrink-0" />
              </a>
            ) : (
              <p className="text-sm text-muted-foreground">Not captured</p>
            )}
          </Section>

          {report.route_history.length > 0 && (
            <Section title="Recent routes">
              <ol className="text-xs space-y-1 font-mono">
                {report.route_history.map((entry, i) => (
                  <li key={i} className="text-muted-foreground">
                    <span className="text-foreground">{entry.path}</span>
                    {" — "}
                    {format(new Date(entry.timestamp), "h:mm:ss a")}
                  </li>
                ))}
              </ol>
            </Section>
          )}

          {report.console_errors.length > 0 && (
            <Section title={`Recent console output (${report.console_errors.length})`}>
              <div className="space-y-2 max-h-64 overflow-y-auto rounded-md border bg-muted/30 p-3">
                {report.console_errors.map((entry, i) => (
                  <div key={i} className="text-xs font-mono">
                    <Badge
                      variant="outline"
                      className={
                        entry.level === "error"
                          ? "bg-red-500/10 text-red-600 border-red-500/20 mr-2"
                          : "bg-yellow-500/10 text-yellow-600 border-yellow-500/20 mr-2"
                      }
                    >
                      {entry.level}
                    </Badge>
                    <span className="text-muted-foreground">
                      {format(new Date(entry.timestamp), "h:mm:ss a")}
                    </span>
                    <p className="mt-1 break-all">{entry.message}</p>
                    {entry.stack && (
                      <pre className="mt-1 text-[10px] text-muted-foreground whitespace-pre-wrap">
                        {entry.stack}
                      </pre>
                    )}
                  </div>
                ))}
              </div>
            </Section>
          )}

          <Section title="Browser">
            <dl className="grid grid-cols-[140px_1fr] gap-y-1 text-xs">
              {report.viewport_width && report.viewport_height && (
                <>
                  <dt className="text-muted-foreground">Viewport</dt>
                  <dd>{report.viewport_width} × {report.viewport_height}</dd>
                </>
              )}
              {report.user_agent && (
                <>
                  <dt className="text-muted-foreground">User agent</dt>
                  <dd className="break-all font-mono">{report.user_agent}</dd>
                </>
              )}
            </dl>
          </Section>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold">{title}</h3>
        {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
      </div>
      {children}
    </div>
  );
}
